import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { SPANISH_GUIDE_PATH } from "@/lib/es-guide";

const nameSchema = z.string().trim().min(1, "Name is required").max(100);
const emailSchema = z.string().trim().email("Invalid email address").max(255);
const phoneSchema = z.string().trim().max(50).optional();
const messageSchema = z
  .string()
  .trim()
  .min(1, "Message is required")
  .max(2000, "Message must be less than 2000 characters");
const turnstileTokenSchema = z
  .string()
  .trim()
  .min(1, "Please complete the security check");

const contactSchema = z.object({
  name: nameSchema,
  phone: phoneSchema,
  email: emailSchema,
  message: messageSchema,
  turnstileToken: turnstileTokenSchema,
});

const appointmentSchema = z.object({
  name: nameSchema,
  phone: phoneSchema,
  email: emailSchema,
  preferredDate: z.string().trim().max(200).optional(),
  message: messageSchema,
  turnstileToken: turnstileTokenSchema,
});

const selfPaySchema = z.object({
  name: nameSchema,
  phone: phoneSchema,
  email: emailSchema,
  procedure: z.string().trim().max(100).optional(),
  message: messageSchema,
  turnstileToken: turnstileTokenSchema,
});

const suggestionSchema = z.object({
  message: messageSchema,
  turnstileToken: turnstileTokenSchema,
});


const spanishGuideSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  /** Route the request came from, used only to tell the office where the lead started. */
  page: z.string().trim().max(200).optional(),
  turnstileToken: turnstileTokenSchema,
});

const storySchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema,
  message: messageSchema,
  turnstileToken: turnstileTokenSchema,
});

async function verifyTurnstileToken(token: string) {
  const secret = process.env.TURNSTILE_SECRET_KEY;
  if (!secret) {
    throw new Error("Turnstile secret key is not configured");
  }

  const response = await fetch(
    "https://challenges.cloudflare.com/turnstile/v0/siteverify",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secret, response: token }),
    }
  );

  const data = (await response.json()) as {
    success: boolean;
    "error-codes"?: string[];
  };

  if (!data.success) {
    throw new Error("Security verification failed. Please try again.");
  }

  return data.success;
}


export const sendContactInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => contactSchema.parse(data))
  .handler(async ({ data }) => {
    await verifyTurnstileToken(data.turnstileToken);
    const { sendSiteInquiry } = await import(
      "@/lib/email/send-site-inquiry.server"
    );
    await sendSiteInquiry({
      source: "Contact Form",
      name: data.name,
      phone: data.phone,
      email: data.email,
      message: data.message,
    });
    return { success: true };
  });

export const sendSuggestionInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => suggestionSchema.parse(data))
  .handler(async ({ data }) => {
    await verifyTurnstileToken(data.turnstileToken);
    const { sendSiteInquiry } = await import(
      "@/lib/email/send-site-inquiry.server"
    );
    await sendSiteInquiry({ source: "Suggestion Box", message: data.message });
    return { success: true };
  });

export const sendAppointmentInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => appointmentSchema.parse(data))
  .handler(async ({ data }) => {
    await verifyTurnstileToken(data.turnstileToken);
    const { sendSiteInquiry } = await import(
      "@/lib/email/send-site-inquiry.server"
    );
    const messageWithDate = data.preferredDate
      ? `Preferred date/time: ${data.preferredDate}\n\n${data.message}`
      : data.message;
    await sendSiteInquiry({
      source: "Appointment Request",
      name: data.name,
      phone: data.phone,
      email: data.email,
      message: messageWithDate,
    });
    return { success: true };
  });

export const sendSelfPayInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => selfPaySchema.parse(data))
  .handler(async ({ data }) => {
    await verifyTurnstileToken(data.turnstileToken);
    const { sendSiteInquiry } = await import(
      "@/lib/email/send-site-inquiry.server"
    );
    await sendSiteInquiry({
      source: "Self-Pay Inquiry",
      name: data.name,
      phone: data.phone,
      email: data.email,
      message: [
        data.procedure ? `Procedure of interest: ${data.procedure}` : null,
        data.message,
      ]
        .filter(Boolean)
        .join("\n\n"),
    });
    return { success: true };
  });


export const sendStoryInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => storySchema.parse(data))
  .handler(async ({ data }) => {
    await verifyTurnstileToken(data.turnstileToken);
    const { sendSiteInquiry } = await import(
      "@/lib/email/send-site-inquiry.server"
    );
    const { turnstileToken: _t, ...inquiry } = data;
    await sendSiteInquiry({ source: "Share Your Story", ...inquiry });
    return { success: true };
  });

/**
 * Spanish lead magnet: the visitor gives a name and email to unlock the
 * downloadable guide. The office is notified so a Spanish-speaking staff
 * member can follow up.
 */
export const requestSpanishGuide = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => spanishGuideSchema.parse(data))
  .handler(async ({ data }) => {
    await verifyTurnstileToken(data.turnstileToken);
    const { sendSiteInquiry } = await import(
      "@/lib/email/send-site-inquiry.server"
    );
    await sendSiteInquiry({
      source: "Descarga de guía en español",
      name: data.name,
      email: data.email,
      phone: data.phone,
      message: [
        "Solicitó la Guía de Cirugía Bariátrica en el Norte de Virginia (PDF en español).",
        data.page ? `Página de origen: ${data.page}` : null,
        "Seguimiento sugerido: llamar en español.",
      ]
        .filter(Boolean)
        .join("\n"),
    });
    return { success: true, download: SPANISH_GUIDE_PATH };
  });
